import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { ProgressStepper, PAYMENT_STEPS } from '../components/ProgressStepper';
import { usePaymentParams } from './PaymentRouter';
import { apiUrl, fetchRateQuote } from '../lib/api';

export function ReviewPage() {
  const navigate = useNavigate();
  const params = usePaymentParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kycStatus, setKycStatus] = useState<'checking' | 'verified' | 'required' | 'error'>('checking');
  const [quote, setQuote] = useState<any>(null);

  useEffect(() => {
    checkKycStatus();
    fetchQuote();
  }, []);

  async function checkKycStatus() {
    const token = params?.at || localStorage.getItem('pouchpay_token');

    if (!token) {
      setKycStatus('error');
      setError('No authentication token found');
      return;
    }

    try {
      // /shared-kyc/kyc/status requires countryCode as a query param — without
      // it Fastify rejects the request with 400 before the handler runs.
      const countryCode = params?.countryCode || 'NG';
      const response = await fetch(
        apiUrl(`/shared-kyc/kyc/status?countryCode=${encodeURIComponent(countryCode)}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw new Error('Failed to check KYC status');
      }

      const data = await response.json();

      if (data.status === 'VERIFIED') {
        setKycStatus('verified');
      } else {
        setKycStatus('required');
      }
    } catch (err: any) {
      console.error('KYC check error:', err);
      setKycStatus('error');
      setError(err.message);
    }
  }

  async function fetchQuote() {
    try {
      if (!params?.amount || !params?.countryCode) return;
      const data = await fetchRateQuote({
        type: params.type === 'offramp' ? 'offramp' : 'onramp',
        amount: params.amount,
        fiatCurrency: getCurrencyForCountry(params.countryCode),
        countryCode: params.countryCode,
      });
      setQuote(data);
    } catch (err: any) {
      console.error('Quote fetch error:', err);
    }
  }

  async function handleCreateSession() {
    if (kycStatus === 'required') {
      // Navigate to KYC page
      navigate('/pay/kyc');
      return;
    }

    if (kycStatus !== 'verified') {
      setError('Please complete KYC verification first');
      return;
    }

    setLoading(true);
    setError(null);

    const token = params?.at || localStorage.getItem('pouchpay_token');

    try {
      // Use correct endpoint based on type
      const endpoint = params?.type === 'offramp'
        ? apiUrl('/shared-kyc/ramp/offramp')
        : apiUrl('/shared-kyc/ramp/onramp');

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        // The ramp endpoint expects cryptoCurrency/cryptoNetwork (not asset/network)
        // and a 3-letter ISO fiat `currency` (NOT the 'local'|'crypto' URL param).
        // source+signature are forwarded so the API can verify the link's
        // authenticity when present.
        body: JSON.stringify({
          source: params?.source,
          signature: params?.signature,
          amount: params?.amount,
          cryptoCurrency: params?.asset,
          cryptoNetwork: params?.network,
          currency: params?.countryCode ? getCurrencyForCountry(params.countryCode) : 'NGN',
          countryCode: params?.countryCode,
          walletAddress: params?.walletAddress,
          quoteId: params?.quoteId
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create session');
      }

      const data = await response.json();

      // The create response carries the authoritative bank/crypto instruction.
      // Stash it so the Instructions page can render it reliably — GET
      // /v2/sessions/:id can drop it once the provider webhook mutates
      // session.providerMeta.
      const instr = data.paymentInstruction || data.cryptoInstruction;
      if (data.sessionId && instr) {
        try {
          sessionStorage.setItem(
            `pouchpay_instruction_${data.sessionId}`,
            JSON.stringify({ type: params?.type, instr })
          );
        } catch { /* sessionStorage may be unavailable */ }
      }

      // Navigate to instructions page with sessionId
      navigate(`/pay/instructions?sessionId=${data.sessionId}`);

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleBack() {
    navigate('/pay/auth');
  }

  const localCurrency = params?.countryCode ? getCurrencyForCountry(params.countryCode) : 'NGN';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Stepper */}
      <ProgressStepper
        steps={PAYMENT_STEPS}
        currentStep="review"
        completedSteps={['amount', 'wallet', 'auth', ...(kycStatus === 'verified' ? ['kyc'] : [])]}
      />

      <div className="max-w-2xl mx-auto px-4 pb-8">
        {/* Custom branding */}
        {params?.logoUrl && (
          <div className="text-center mb-6">
            <img
              src={params.logoUrl}
              alt="Logo"
              className="h-12 w-auto mx-auto"
            />
          </div>
        )}

        <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
          Review Your Order
        </h1>
        <p className="text-center text-gray-600 mb-6">
          Please confirm the details below before proceeding
        </p>

        {/* Main card */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* KYC Status */}
          <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
            kycStatus === 'verified'
              ? 'bg-green-50 border border-green-200'
              : kycStatus === 'required'
              ? 'bg-yellow-50 border border-yellow-200'
              : kycStatus === 'checking'
              ? 'bg-gray-50 border border-gray-200'
              : 'bg-red-50 border border-red-200'
          }`}>
            {kycStatus === 'verified' ? (
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : kycStatus === 'checking' ? (
              <Loader2 className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5 animate-spin" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`text-sm font-semibold ${
                kycStatus === 'verified' ? 'text-green-800' :
                kycStatus === 'checking' ? 'text-gray-700' : 'text-yellow-800'
              }`}>
                {kycStatus === 'verified' ? 'Identity Verified' :
                 kycStatus === 'checking' ? 'Checking verification status...' :
                 kycStatus === 'required' ? 'ID Verification Required' : 'Verification Error'}
              </p>
              {kycStatus === 'required' && (
                <p className="text-sm text-yellow-700 mt-1">
                  You'll need to verify your identity before proceeding with payment
                </p>
              )}
            </div>
          </div>

          {/* Order Details */}
          <div className="space-y-4 mb-6">
            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Type</span>
              <span className="font-semibold text-gray-900">
                {params?.type === 'onramp' ? 'Buy Crypto' : 'Sell Crypto'}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">You Pay</span>
              <span className="font-semibold text-gray-900">
                {localCurrency} {params?.amount?.toLocaleString()}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">You Receive</span>
              <span className="font-semibold text-gray-900">
                {quote?.cryptoAmount ? Number(quote.cryptoAmount).toFixed(4) : '...'} {params?.asset}
              </span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Network</span>
              <span className="font-semibold text-gray-900">{params?.network}</span>
            </div>

            <div className="flex justify-between items-center py-3 border-b">
              <span className="text-gray-600">Wallet Address</span>
              <span className="font-mono text-sm text-gray-900 break-all">
                {params?.walletAddress?.slice(0, 10)}...{params?.walletAddress?.slice(-8)}
              </span>
            </div>

            {quote?.feePercent && (
              <div className="flex justify-between items-center py-3 border-b">
                <span className="text-gray-600">Fee</span>
                <span className="font-semibold text-gray-900">{quote.feePercent}%</span>
              </div>
            )}

            {quote?.rate && (
              <div className="flex justify-between items-center py-3">
                <span className="text-gray-600">Exchange Rate</span>
                <span className="text-sm text-gray-700">
                  1 {params?.asset} ≈ {Number(quote.rate).toLocaleString()} {localCurrency}
                </span>
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleBack}
              className="flex-1 py-3 px-6 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleCreateSession}
              disabled={loading || kycStatus === 'checking'}
              className="flex-2 py-3 px-6 bg-brand text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-opacity"
              style={{ flex: 2 }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating Order...
                </>
              ) : kycStatus === 'required' ? (
                'Continue to Verification'
              ) : (
                'Confirm & Continue'
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Powered by{' '}
            <a
              href="https://pouchfinance.xyz"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand hover:text-brand-600 font-medium"
            >
              PouchPay
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function getCurrencyForCountry(countryCode: string): string {
  const currencyMap: Record<string, string> = {
    NG: 'NGN',
    KE: 'KES',
    GH: 'GHS',
    ZA: 'ZAR',
    UG: 'UGX'
  };
  return currencyMap[countryCode] || 'USD';
}
